import { FC, useEffect } from 'react'
import { ServiceUserDetails } from '../../formComponents'
import { Home, HomeDto, Resident, ResidentStatus } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { getNominative } from 'common/utils'
import { ModalSectionProps } from '../utils'
import { getFundingSourceName } from 'screens/Rostering/utils'
import { getNewAddress } from '../utils'
import { Avatar } from '@lmc/ui-kit'
import { useGetResidentFunding } from 'screens/Rostering/hooks/useResidentFunding'
import { useGetFunders } from 'screens/Rostering/hooks/useFunders'
import { useGetInvoicingRateCards } from 'screens/Rostering/hooks/useInvoicingRates'
import { resolveFundingChain } from 'screens/Rostering/utils'
import { InvoicingRateSection } from 'screens/Rostering/components/InvoicingRateSection'
import useFeatureFlags from 'hooks/useFeatureFlags'

type ServiceUserDetailsSectionProps = ModalSectionProps & {
  disableResident?: boolean
  disableAddress?: boolean
  showInvoicingOverride?: boolean
}

export const ServiceUserDetailsSection: FC<ServiceUserDetailsSectionProps> = ({
  formData,
  form,
  disableResident,
  disableAddress,
  showInvoicingOverride,
}) => {
  const flags = useFeatureFlags({
    flagNames: ['invoicing'],
  })?.data
  const invoicingEnabled = flags?.invoicing?.enabled

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const { residents, refetchResidents } = useResidentsList({})

  useEffect(() => {
    refetchResidents()
  }, [])

  const groupedResidents: { [status: string]: Resident[] } = {
    [ResidentStatus.active]: [],
    [ResidentStatus.inactive]: [],
    [ResidentStatus.hospitalised]: [],
    [ResidentStatus.deceased]: [],
    [ResidentStatus.discharged]: [],
  }

  residents?.forEach((resident: Resident) => {
    const { status } = resident
    if (!groupedResidents[status]) {
      groupedResidents[status] = []
    }
    groupedResidents[status].push(resident)
  })

  Object.keys(groupedResidents).forEach((key) => {
    if (groupedResidents[key].length === 0) {
      delete groupedResidents[key]
    } else {
      groupedResidents[key].sort((a, b) => {
        const nameA = `${a.name.first} ${a.name.last}`
        const nameB = `${b.name.first} ${b.name.last}`
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedResidentOptions =
    Object.keys(groupedResidents).map((status) => ({
      label: status.toLocaleUpperCase(),
      options: groupedResidents[status].map((resident) => ({
        label: `${resident.name.first} ${resident.name.last}`,
        value: resident.id,
        image: <Avatar url={resident.picture as unknown as string} size={20} />,
      })),
    })) || []

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const selectedResident = formData?.subjectId

  const foundResident = residents?.find(
    (resident) => resident?.id === selectedResident
  )

  const hasValidFundingSources =
    foundResident?.carePackage?.fundingSources instanceof Array

  const { data: residentFundingRecords = [] } = useGetResidentFunding(selectedResident ?? '')
  const { data: allFunders = [] } = useGetFunders()
  const { data: rateCards = [] } = useGetInvoicingRateCards()

  const atDate = formData?.startTime ?? formData?.patternStart

  // Build options from new ResidentFunding records (preferred),
  // falling back to legacy carePackage.fundingSources.
  // Label includes the active rate so users can see e.g. "NHS — Standard (GBP 18/hour)"
  const newFundingOptions = residentFundingRecords
    .map((record) => {
      const funder = allFunders.find((f) => f._id === record.funderId)
      if (!funder) return null
      const { rateCard, rateCardLabel } = resolveFundingChain(
        funder._id,
        allFunders,
        residentFundingRecords,
        rateCards,
        atDate,
      )
      const label = rateCard && rateCardLabel
        ? `${funder.name} — ${rateCard.name} (${rateCardLabel})`
        : funder.name
      return { value: funder._id, label }
    })
    .filter(Boolean) as { value: string; label: string }[]

  const legacyFundingOptions = hasValidFundingSources
    ? (foundResident?.carePackage?.fundingSources?.map((source) => ({
        value: source.type,
        label: getFundingSourceName(source, home),
      })) ?? [])
    : []

  const fundingTypeOptions = newFundingOptions.length > 0 ? newFundingOptions : legacyFundingOptions

  useEffect(() => {
    if (form?.isFieldsTouched()) {
      form?.setFieldValue('fundingTypeId', undefined)
      form?.setFieldValue('addressId', undefined)
    }
  }, [selectedResident, form])

  // Auto-select runs separately so it fires when async options arrive, not just on
  // resident change. Uses the first option's value as a dep to stay stable once loaded.
  const singleOptionValue = fundingTypeOptions.length === 1 ? fundingTypeOptions[0].value : undefined
  useEffect(() => {
    if (singleOptionValue) {
      form?.setFieldValue('fundingTypeId', singleOptionValue)
    }
  }, [selectedResident, singleOptionValue, form])

  // Auto-map old fundingTypeId (legacy ObjectId) → new Funder._id via externalFundingTypeId
  useEffect(() => {
    const currentId = formData?.fundingTypeId
    if (!currentId || !allFunders.length) return
    if (allFunders.some((f) => f._id === currentId)) return
    const matched = allFunders.find((f) => f.externalFundingTypeId === currentId)
    if (matched) {
      form?.setFieldValue('fundingTypeId', matched._id)
    }
  }, [formData?.fundingTypeId, allFunders])

  const addressOptions = hasValidFundingSources
    ? foundResident?.addresses?.map((address) => ({
        // @ts-ignore
        // eslint-disable-next-line no-underscore-dangle
        value: `${address?._id || address?.suburb || address?.name}`,
        label: `${getNewAddress(address, true)}`,
      }))
    : []

  return (
    <ServiceUserDetails
      preferredPeopleNominative={getNominative(
        preferredPeopleNominative,
        'Resident'
      )}
      residentOptions={groupedResidentOptions}
      fundingTypeOptions={fundingTypeOptions}
      addressOptions={addressOptions}
      disableResident={disableResident}
      disableAddress={disableAddress}
      formData={formData}
      afterFundingType={
        showInvoicingOverride && invoicingEnabled ? (
          <InvoicingRateSection
            residentId={selectedResident}
            atDate={formData?.startTime}
            savedOverride={formData?.invoicingRateOverride}
          />
        ) : undefined
      }
    />
  )
}
